import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, PenLine } from 'lucide-react';

// Henry-Griffitts' countersignature — captured ONCE and reused at every approval.
//
// This is Randall's actual signature, so it is treated as a credential rather than an
// image asset: it is stored with authenticated Cloudinary delivery, only its public_id is
// persisted, and the preview below is proxied behind auth. Nothing in the app generates or
// defaults a signature — a missing one blocks approval rather than producing a document
// signed by nobody.
const API = '/api';

export default function CountersignatureSetup({ onSaved }) {
  const [current, setCurrent] = useState(null);   // null | {configured, signer_name, ...}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [drawing, setDrawing] = useState(false);  // has anything been drawn
  const [previewKey, setPreviewKey] = useState(0);

  const canvasRef = useRef(null);
  const isDown = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/countersignature`);
      setCurrent(r.data);
      if (r.data?.signer_name) setName(r.data.signer_name);
      if (r.data?.signer_title) setTitle(r.data.signer_title);
    } catch {
      setCurrent({ configured: false });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1A3A6B';
  }, [loading]);

  const pt = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches && e.touches.length ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); isDown.current = true; last.current = pt(e); };
  const move = (e) => {
    if (!isDown.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pt(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!drawing) setDrawing(true);
  };
  const end = (e) => { if (e) e.preventDefault(); isDown.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setDrawing(false);
  };

  const save = async () => {
    if (!drawing || saving) return;
    if (!name.trim() || !title.trim()) { setError('Name and title are both required — they print on the execution block.'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/countersignature`, {
        image: canvasRef.current.toDataURL('image/png'),
        signer_name: name.trim(),
        signer_title: title.trim(),
      });
      clear();
      await load();
      setPreviewKey((k) => k + 1);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save the signature.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-xs font-sans text-hg-text-dim flex items-center gap-2">
      <Loader2 size={13} className="animate-spin" /> Loading countersignature…
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="countersignature-setup">
      <div>
        <h2 className="text-sm font-sans font-semibold text-hg-text">Henry-Griffitts countersignature</h2>
        <p className="text-[11px] font-sans text-hg-text-dim mt-1 max-w-2xl">
          Captured once and applied to every agreement you approve. Approving an agreement
          signs it on Henry-Griffitts&rsquo; behalf, so this signature is what executes it.
          It is stored privately and is never published.
        </p>
      </div>

      {current?.configured ? (
        <div className="border border-hg-border rounded-sm p-3 bg-hg-surface" data-testid="cs-current">
          <div className="flex items-center gap-2 text-xs font-sans text-hg-green">
            <CheckCircle2 size={13} /> On file
          </div>
          <div className="mt-2 text-xs font-sans text-hg-text">
            {current.signer_name} — {current.signer_title}
          </div>
          <div className="text-[11px] font-sans text-hg-text-dim">
            {current.licensor_name} · captured {String(current.uploaded_at || '').slice(0, 10)}
            {current.uploaded_by ? ` by ${current.uploaded_by}` : ''}
          </div>
          <img
            key={previewKey}
            src={`${API}/countersignature/preview?v=${previewKey}`}
            alt="Countersignature on file"
            className="mt-2 bg-white rounded-sm p-2"
            style={{ maxHeight: 70 }}
          />
        </div>
      ) : (
        <div className="border border-amber-400/50 bg-amber-400/10 rounded-sm p-3 text-xs font-sans text-amber-200"
             data-testid="cs-missing">
          No countersignature on file. Approvals are blocked until one is captured — approving
          executes the agreement on Henry-Griffitts&rsquo; behalf, and a contract signed by
          nobody is worse than one not yet signed.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Signatory name</label>
          <input
            data-testid="cs-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Randall Henry"
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm font-sans text-hg-text"
          />
        </div>
        <div>
          <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Title</label>
          <input
            data-testid="cs-title" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="CGO"
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm font-sans text-hg-text"
          />
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">
          {current?.configured ? 'Draw a new signature to replace the one on file' : 'Draw your signature'}
        </label>
        <div className="bg-white rounded-sm overflow-hidden border border-hg-border">
          <canvas
            ref={canvasRef}
            data-testid="cs-pad"
            style={{ display: 'block', width: '100%', height: 130, touchAction: 'none', cursor: 'crosshair' }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
        </div>
        <div className="flex items-center gap-3 mt-2">
          <button
            data-testid="cs-save" onClick={save} disabled={!drawing || saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <PenLine size={13} />}
            {current?.configured ? 'Replace signature' : 'Save signature'}
          </button>
          <button onClick={clear} className="text-[11px] font-sans text-hg-text-dim underline">Clear</button>
        </div>
        {error && <div className="mt-2 text-xs font-sans text-red-400" data-testid="cs-error">{error}</div>}
      </div>
    </div>
  );
}
